import { useMemo, useState } from 'react'
import { currencyText } from '@billiard/shared'
import type {
  AnalyticsReport,
  AnalyticsHourRow,
  AnalyticsWeekdayRow,
  ReportBreakdownRow
} from '@billiard/shared'
import { useFetch } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  AsyncState,
  BarChart,
  Button,
  Delta,
  EmptyState,
  Heatmap,
  Input,
  Kpi,
  PageHead,
  SectionHead,
  type BarDatum,
  type HeatCell
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import { count, duration, money, msToDateInput, share } from '@/lib/format'
import { Cur, Money, useCurrency } from '@/lib/currency'
import {
  PERIOD_UNITS,
  SHIFT_LABELS,
  resolveRange,
  type PeriodShift,
  type PeriodUnit
} from '@/lib/period'

// Экран «Аналитика» — ответ на вопрос «когда и где мы зарабатываем».
//
// Отчёты (/reports) отвечают «сколько», Продажи (/sales) дают список чеков.
// Здесь — распределение выручки по часам, дням недели и столам плюс тепловая
// карта «день недели × час»: по ней видно, какие вечера действительно пиковые,
// а какие часы клуб держит зал открытым впустую.
//
// Вся агрегация — на бэкенде (GET /v1/reports/analytics), в часовом поясе клуба.
// Чек относится к моменту ОТКРЫТИЯ стола: пик продаж — это время прихода
// гостей, а не время расчёта.

/** Что показывать в графиках по часам и дням недели. */
type Metric = 'amount' | 'count' | 'minutes'

const METRICS: ReadonlyArray<{ id: Metric; label: string }> = [
  { id: 'amount', label: 'Выручка' },
  { id: 'count', label: 'Чеки' },
  { id: 'minutes', label: 'Занятость' }
]

const WEEKDAY_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
const WEEKDAY_FULL = [
  'Понедельник',
  'Вторник',
  'Среда',
  'Четверг',
  'Пятница',
  'Суббота',
  'Воскресенье'
]

const DAY_MS = 24 * 60 * 60 * 1000

const METHOD_LABEL: Record<string, string> = {
  cash: 'Наличные',
  card: 'Безналичные',
  mixed: 'Сборная',
  unknown: 'Не указан'
}

function hourLabel(h: number): string {
  return `${String(h).padStart(2, '0')}:00`
}

/** Короткая дата из ISO-дня (YYYY-MM-DD) без разбора в Date — день уже в TZ клуба. */
function dayShort(day: string): string {
  const [, m, d] = day.split('-')
  return `${d}.${m}`
}

function metricValue(row: { amount: number; count: number; minutes: number }, m: Metric): number {
  return m === 'amount' ? row.amount : m === 'count' ? row.count : row.minutes
}

export function Analytics(): React.JSX.Element {
  const [unit, setUnit] = useState<PeriodUnit>('day')
  const [shift, setShift] = useState<PeriodShift>('current')
  const [customFrom, setCustomFrom] = useState(() => msToDateInput(Date.now() - 30 * DAY_MS))
  const [customTo, setCustomTo] = useState(() => msToDateInput(Date.now()))
  const [metric, setMetric] = useState<Metric>('amount')
  const cur = currencyText(useCurrency())

  // «Сейчас» зафиксировано и двигается только по кнопке «Обновить»: иначе from/to
  // менялись бы на каждый рендер и useFetch крутил бы запросы без остановки.
  const [nowMinute, setNowMinute] = useState(() => Math.floor(Date.now() / 60_000) * 60_000)
  const range = useMemo(
    () => resolveRange(unit, shift, customFrom, customTo, nowMinute),
    [unit, shift, customFrom, customTo, nowMinute]
  )

  const query = useConfigQuery()
  const report = useFetch<AnalyticsReport>(
    '/v1/reports/analytics',
    { ...query, from: range.from, to: range.to },
    [query?.clubId, range.from, range.to]
  )

  const data = report.data
  const t = data?.totals

  // Для незакончившегося периода «Обновить» ещё и сдвигает правую границу окна;
  // если минута не сменилась, просто перезапрашиваем те же данные.
  const refresh = (): void => {
    const next = Math.floor(Date.now() / 60_000) * 60_000
    if (next === nowMinute) report.reload()
    else setNowMinute(next)
  }

  return (
    <>
      <PageHead
        eyebrow="Аналитика"
        title="Аналитика продаж"
        lead="Когда и на каких столах клуб зарабатывает: выручка по часам, дням недели и дням периода, тепловая карта загрузки и разбивки по столам, тарифам и операторам."
      />
      <ClubGate>
        <div className="toolbar">
          <div className="tabs">
            {PERIOD_UNITS.map((p) => (
              <button
                key={p.unit}
                className={`tab${unit === p.unit ? ' is-active' : ''}`}
                onClick={() => setUnit(p.unit)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="toolbar toolbar--compact">
          <div className="toolbar__left">
            {unit === 'custom' ? (
              <>
                <label className="field__label" htmlFor="an-from">
                  С даты
                </label>
                <Input
                  id="an-from"
                  type="date"
                  value={customFrom}
                  max={customTo}
                  onChange={(e) => setCustomFrom(e.target.value)}
                />
                <label className="field__label" htmlFor="an-to">
                  По дату
                </label>
                <Input
                  id="an-to"
                  type="date"
                  value={customTo}
                  min={customFrom}
                  onChange={(e) => setCustomTo(e.target.value)}
                />
              </>
            ) : (
              <div className="tabs tabs--sm">
                <button
                  className={`tab${shift === 'current' ? ' is-active' : ''}`}
                  onClick={() => setShift('current')}
                >
                  {SHIFT_LABELS[unit].current}
                </button>
                <button
                  className={`tab${shift === 'previous' ? ' is-active' : ''}`}
                  onClick={() => setShift('previous')}
                >
                  {SHIFT_LABELS[unit].previous}
                </button>
              </div>
            )}
          </div>
          <div className="toolbar__right">
            <span className="muted">
              {range.label}
              {range.partial ? ' · на текущий момент' : ''}
            </span>
            <Button variant="ghost" onClick={refresh} disabled={report.loading}>
              {report.loading ? <span className="spin" /> : null}
              Обновить
            </Button>
          </div>
        </div>

        <AsyncState query={report} loadingLabel="Считаем аналитику…" />

        {data && t && (
          <>
            <SectionHead
              title="Итоги периода"
              meta={`Сравнение — с предыдущим окном той же длины (${count(t.days)} дн.)`}
            />
            <div className="kpi-grid">
              <Kpi
                label="Выручка"
                value={<Money value={t.revenue} />}
                gold
                accent
                sub={<Delta current={t.revenue} prev={data.previous.totals.revenue} />}
              />
              <Kpi
                label="Чеков"
                value={count(t.count)}
                sub={<Delta current={t.count} prev={data.previous.totals.count} />}
              />
              <Kpi
                label="Средний чек"
                value={<Money value={t.avgCheck} />}
                sub={`Средняя игра — ${duration(t.avgDurationMin)}`}
              />
              <Kpi
                label="Выручка за день"
                value={<Money value={t.avgPerDay} />}
                sub={`Дней с продажами: ${count(t.activeDays)} из ${count(t.days)}`}
              />
              <Kpi label="Наличные" value={<Money value={t.cash} />} />
              <Kpi label="Безналичные" value={<Money value={t.card} />} />
              <Kpi
                label="Часов игры"
                value={duration(t.minutes)}
                sub={`Рабочих часов клуба: ${count(t.openHours)}`}
              />
              <Kpi
                label="Бар и кухня"
                value={<Money value={t.barRevenue} />}
                sub={t.revenue ? `${share(t.barRevenue, t.revenue)} выручки` : undefined}
              />
              <Kpi
                label="Скидки и бонусы"
                value={<Money value={t.discount + t.bonusUsed} />}
                sub={`Скидки ${money(t.discount)}, бонусы ${money(t.bonusUsed)}`}
              />
              <Kpi
                label="Возвраты"
                value={<Money value={t.refundsTotal} />}
                sub={`${count(t.refundsCount)} шт. · нетто ${money(t.netRevenue)}`}
              />
            </div>

            <SectionHead title="Пики периода" meta="Максимум выручки" />
            <div className="kpi-grid">
              <Kpi
                label="Лучший день"
                value={data.peaks.bestDay ? dayShort(data.peaks.bestDay.day) : '—'}
                gold
                sub={
                  data.peaks.bestDay
                    ? `${money(data.peaks.bestDay.amount)} ${cur} · ${count(data.peaks.bestDay.count)} чек(ов)`
                    : 'Нет продаж'
                }
              />
              <Kpi
                label="Лучший день недели"
                value={
                  data.peaks.bestWeekday ? WEEKDAY_FULL[data.peaks.bestWeekday.weekday - 1] : '—'
                }
                gold
                sub={
                  data.peaks.bestWeekday
                    ? `${money(data.peaks.bestWeekday.amount)} ${cur} за ${count(data.peaks.bestWeekday.days)} дн.`
                    : 'Нет продаж'
                }
              />
              <Kpi
                label="Пиковый час"
                value={data.peaks.bestHour ? hourLabel(data.peaks.bestHour.hour) : '—'}
                gold
                sub={
                  data.peaks.bestHour
                    ? `${money(data.peaks.bestHour.amount)} ${cur} · ${count(data.peaks.bestHour.count)} чек(ов)`
                    : 'Нет продаж'
                }
              />
            </div>

            <div className="card" style={{ marginTop: 18 }}>
              <SectionHead
                title="Выручка по дням"
                meta={`${count(data.byDay.length)} дн. в периоде`}
              />
              <DayChart data={data} />
            </div>

            <div className="card">
              <div className="toolbar toolbar--compact">
                <div className="toolbar__left">
                  <div className="tabs tabs--sm">
                    {METRICS.map((m) => (
                      <button
                        key={m.id}
                        className={`tab${metric === m.id ? ' is-active' : ''}`}
                        onClick={() => setMetric(m.id)}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="toolbar__right">
                  <span className="muted">
                    {metric === 'minutes'
                      ? 'Занятость — фактические минуты игры, разнесённые по часам'
                      : 'Чек отнесён к часу открытия стола'}
                  </span>
                </div>
              </div>

              <SectionHead title="Продажи по часам суток" />
              <HourChart rows={data.byHour} metric={metric} />

              <SectionHead title="Продажи по дням недели" />
              <WeekdayChart rows={data.byWeekday} metric={metric} />
            </div>

            <div className="card">
              <SectionHead
                title="Тепловая карта: день недели × час"
                meta="Чем ярче ячейка — тем больше выручка"
              />
              <HeatBlock data={data} />
            </div>

            <div className="card">
              <SectionHead title="По столам" meta={`${count(data.byTable.length)} стол(ов)`} />
              <TablesTable data={data} />
            </div>

            <div className="grid" style={{ marginTop: 18 }}>
              <Breakdown title="По тарифам" rows={data.byTariff} />
              <Breakdown title="По типам игр" rows={data.byGameType} />
              <Breakdown title="По операторам" rows={data.byOperator} />
            </div>

            <div className="grid" style={{ marginTop: 18 }}>
              <Breakdown
                title="По способам оплаты"
                rows={data.byMethod.map((r) => ({ ...r, name: METHOD_LABEL[r.name] ?? r.name }))}
              />
              <Breakdown
                title="Бар и кухня: топ позиций"
                rows={data.topMenuItems}
                countLabel="Штук"
              />
            </div>
          </>
        )}
      </ClubGate>
    </>
  )
}

// ───────────────────────── графики ─────────────────────────

function DayChart({ data }: { data: AnalyticsReport }): React.JSX.Element {
  const cur = currencyText(useCurrency())
  const peak = data.peaks.bestDay?.day
  const bars: BarDatum[] = data.byDay.map((d) => ({
    key: d.day,
    label: dayShort(d.day),
    value: d.amount,
    peak: d.day === peak,
    title: `${WEEKDAY_SHORT[d.weekday - 1]} ${dayShort(d.day)} — ${money(d.amount)} ${cur}, ${count(d.count)} чек(ов)`
  }))
  if (bars.length === 0) return <EmptyState message="Нет продаж за период." />
  return <BarChart data={bars} maxTicks={20} height={200} />
}

function HourChart({
  rows,
  metric
}: {
  rows: AnalyticsHourRow[]
  metric: Metric
}): React.JSX.Element {
  const cur = currencyText(useCurrency())
  const max = rows.reduce((m, r) => Math.max(m, metricValue(r, metric)), 0)
  const bars: BarDatum[] = rows.map((r) => {
    const v = metricValue(r, metric)
    return {
      key: String(r.hour),
      label: String(r.hour).padStart(2, '0'),
      value: v,
      peak: v > 0 && v === max,
      title: `${hourLabel(r.hour)} — ${money(r.amount)} ${cur}, ${count(r.count)} чек(ов), занятость ${duration(r.minutes)}`
    }
  })
  if (max === 0) return <EmptyState message="Нет данных по часам." />
  return <BarChart data={bars} maxTicks={24} height={170} minColWidth={18} />
}

function WeekdayChart({
  rows,
  metric
}: {
  rows: AnalyticsWeekdayRow[]
  metric: Metric
}): React.JSX.Element {
  const cur = currencyText(useCurrency())
  const max = rows.reduce((m, r) => Math.max(m, metricValue(r, metric)), 0)
  const bars: BarDatum[] = rows.map((r) => {
    const v = metricValue(r, metric)
    return {
      key: String(r.weekday),
      label: WEEKDAY_SHORT[r.weekday - 1],
      value: v,
      peak: v > 0 && v === max,
      title: `${WEEKDAY_FULL[r.weekday - 1]} (${count(r.days)} дн. в периоде) — ${money(r.amount)} ${cur}, ${count(r.count)} чек(ов), занятость ${duration(r.minutes)}`
    }
  })
  if (max === 0) return <EmptyState message="Нет данных по дням недели." />
  return <BarChart data={bars} maxTicks={7} height={150} minColWidth={40} />
}

function HeatBlock({ data }: { data: AnalyticsReport }): React.JSX.Element {
  const cur = currencyText(useCurrency())
  const cells: HeatCell[] = data.heatmap.map((c) => ({
    row: c.weekday - 1,
    col: c.hour,
    value: c.amount,
    title: `${WEEKDAY_FULL[c.weekday - 1]}, ${hourLabel(c.hour)} — ${money(c.amount)} ${cur}, ${count(c.count)} чек(ов)`
  }))
  if (cells.length === 0) return <EmptyState message="Нет продаж за период." />
  const maxCell = cells.reduce((m, c) => Math.max(m, c.value), 0)
  return (
    <Heatmap
      rows={WEEKDAY_SHORT}
      cols={Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0'))}
      cells={cells}
      legend={
        <>
          Максимум ячейки: {money(maxCell)} <Cur />. Пустая ячейка — в этот час продаж не было.
        </>
      }
    />
  )
}

// ───────────────────────── таблицы ─────────────────────────

function TablesTable({ data }: { data: AnalyticsReport }): React.JSX.Element {
  const total = data.byTable.reduce((s, r) => s + r.amount, 0)
  if (data.byTable.length === 0) return <EmptyState message="Столы не заведены." />
  return (
    <div className="table-wrap">
      <table className="rtable">
        <thead>
          <tr>
            <th>Стол</th>
            <th>Тип игры</th>
            <th className="num">Чеков</th>
            <th className="num">Выручка</th>
            <th className="num">Доля</th>
            <th className="num">Средний чек</th>
            <th className="num">Часов игры</th>
            <th>Загрузка</th>
          </tr>
        </thead>
        <tbody>
          {data.byTable.map((r) => (
            <tr key={r.id || r.name}>
              <td>{r.name}</td>
              <td>{r.kind || '—'}</td>
              <td className="num">{count(r.count)}</td>
              <td className="num">
                {money(r.amount)} <Cur />
              </td>
              <td className="num">{share(r.amount, total)}</td>
              <td className="num">
                {money(r.avgCheck)} <Cur />
              </td>
              <td className="num">{duration(r.minutes)}</td>
              <td>
                <span className="meter" title={`${r.utilization} % от рабочих часов клуба`}>
                  <span
                    className="meter__fill"
                    style={{ width: `${Math.min(100, r.utilization)}%` }}
                  />
                  <span className="meter__val">{r.utilization} %</span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2}>Итого</td>
            <td className="num">{count(data.byTable.reduce((s, r) => s + r.count, 0))}</td>
            <td className="num">
              {money(total)} <Cur />
            </td>
            <td className="num">100 %</td>
            <td className="num">
              {money(data.totals.avgCheck)} <Cur />
            </td>
            <td className="num">{duration(data.totals.minutes)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

function Breakdown({
  title,
  rows,
  countLabel = 'Кол-во'
}: {
  title: string
  rows: ReportBreakdownRow[]
  countLabel?: string
}): React.JSX.Element {
  const total = rows.reduce((s, r) => s + r.amount, 0)
  return (
    <div>
      <SectionHead title={title} meta={`${count(rows.length)} поз.`} />
      {rows.length === 0 ? (
        <EmptyState message="Нет данных за период." />
      ) : (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Наименование</th>
                <th className="num">{countLabel}</th>
                <th className="num">Сумма</th>
                <th className="num">Доля</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id || r.name}>
                  <td>{r.name || '—'}</td>
                  <td className="num">{count(r.count)}</td>
                  <td className="num">
                    {money(r.amount)} <Cur />
                  </td>
                  <td className="num">{share(r.amount, total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
